import { useEffect, useState } from "react";
import { Megaphone, Link2, Database, Bot, CheckCircle2, AlertTriangle } from "lucide-react";
import api from "@/lib/api";
import { Badge } from "@/components/ui/badge";

function Channel({ icon: Icon, name, status, detail, ready }) {
  return <div className="rounded-lg border bg-background p-3">
    <div className="flex items-center justify-between gap-2">
      <div className="flex items-center gap-1.5 text-sm font-semibold"><Icon className="h-4 w-4 text-[#315A7D]" />{name}</div>
      <Badge variant="outline" className={ready ? "text-emerald-700" : "text-amber-700"}>{status}</Badge>
    </div>
    <p className="mt-2 text-[11px] leading-relaxed text-muted-foreground">{detail}</p>
  </div>;
}

export default function GrowthIntegrationStatus() {
  const [sms, setSms] = useState(null);
  const [openai, setOpenai] = useState(null);
  const [voice, setVoice] = useState(null);

  useEffect(() => {
    Promise.allSettled([
      api.get("/sms/provider-status"), api.get("/copilot/provider-status"), api.get("/voice-ai/auto/status"),
    ]).then(([s, o, v]) => {
      if (s.status === "fulfilled") setSms(s.value.data);
      if (o.status === "fulfilled") setOpenai(o.value.data);
      if (v.status === "fulfilled") setVoice(v.value.data);
    });
  }, []);

  return (
    <section className="mb-5 rounded-xl border bg-card p-5 shadow-sm" data-testid="growth-channel-status">
      <div className="flex flex-wrap items-center justify-between gap-2">
        <div>
          <h2 className="font-heading text-lg font-bold">Growth Channels & Connections</h2>
          <p className="mt-1 text-sm text-muted-foreground">What Fortunate Ops can actually launch or receive today — without implying a connection that does not exist.</p>
        </div>
      </div>
      <div className="mt-4 grid gap-3 sm:grid-cols-2 lg:grid-cols-4">
        <Channel icon={Megaphone} name="Meta Ads" status="Not connected" ready={false}
          detail="No Meta Marketing API / Lead Ads connection is configured yet. Current Growth drafts do not create Meta ads, spend budget, or sync Meta lead forms." />
        <Channel icon={Link2} name="Fortunate Landing Pages" status="Ready" ready
          detail="Agency-owned public lead-capture pages, consent capture, routing and appointment booking are available now." />
        <Channel icon={Database} name="Database Reactivation" status={sms?.configured ? "SMS ready" : "Twilio needed"} ready={!!sms?.configured}
          detail="Existing-book campaigns can select, suppress and route eligible records. SMS execution depends on the agency Twilio connection." />
        <Channel icon={Bot} name="AI Outreach" status={voice?.agency_enabled ? "Automatic" : openai?.configured ? "AI ready" : "OpenAI needed"} ready={!!openai?.configured}
          detail="OpenAI drafts campaign content and Follow Up Now messages. Eligible overdue voice follow-ups can run automatically when the agency backlog worker is enabled." />
      </div>
      <div className="mt-3 flex items-start gap-2 rounded-md bg-muted/50 p-3 text-xs text-muted-foreground">
        {sms?.configured || openai?.configured ? <CheckCircle2 className="mt-0.5 h-4 w-4 shrink-0 text-emerald-600" /> : <AlertTriangle className="mt-0.5 h-4 w-4 shrink-0 text-amber-600" />}
        <span><strong className="text-foreground">Meta is intentionally shown as Not connected.</strong> A campaign labeled for a Fortunate landing page remains a Fortunate-hosted campaign until a real Meta account connection is added.</span>
      </div>
    </section>
  );
}
